import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDate, fmtMoney, PHASE_LABELS, PROJECT_HEALTH, PROJECT_STATUS } from "../../lib/labels";
import { useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function UserSelect({ label, value, onChange, users, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function NewProjectDialog({ open, onClose, onDone }) {
  const { users } = useUsers();
  const [form, setForm] = useState({
    name: "", project_type: "desarrollo_residencial", municipality: "", region: "", client: "",
    sponsor_id: "", director_id: "", pm_id: "", cost_manager_id: "", planner_id: "",
    technical_manager_id: "", quality_manager_id: "", permits_owner_id: "", hs_reviewer_id: "",
    original_budget: "", planned_start: "", baseline_completion: "",
    objective: "", scope: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/proyectos", { ...form, original_budget: form.original_budget ? Number(form.original_budget) : undefined });
      toast.success(`Proyecto ${r.data.project_code} creado en borrador — completa el charter y solicita aprobación`);
      onDone(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const US = ({ label, field, testid }) => <UserSelect label={label} value={form[field]} onChange={(v) => set(field, v)} users={users} testid={testid} />;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="new-project-dialog">
        <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name" /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
              <SelectTrigger data-testid="project-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["desarrollo_residencial", "Desarrollo residencial"], ["oficinas", "Oficinas"], ["hotelero", "Hotelero"], ["logistico", "Logístico"], ["data_center", "Data center"], ["student_housing", "Student housing"], ["senior_living", "Senior living"], ["mixed_use", "Mixed-use"], ["rehabilitacion", "Rehabilitación"]].map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Municipio</Label><Input value={form.municipality} onChange={(e) => set("municipality", e.target.value)} data-testid="project-municipality" /></div>
          <UserSelect label="Project Sponsor *" field="sponsor_id" testid="project-sponsor" />
          <UserSelect label="Project Director *" field="director_id" testid="project-director" />
          <UserSelect label="Project Manager *" field="pm_id" testid="project-pm" />
          <UserSelect label="Cost Manager" field="cost_manager_id" testid="project-cost" />
          <UserSelect label="Planner" field="planner_id" testid="project-planner" />
          <UserSelect label="Technical Manager" field="technical_manager_id" testid="project-tech" />
          <UserSelect label="Quality Manager" field="quality_manager_id" testid="project-quality" />
          <UserSelect label="Permits Owner" field="permits_owner_id" testid="project-permits" />
          <UserSelect label="H&S Reviewer" field="hs_reviewer_id" testid="project-hs" />
          <div><Label>Cliente</Label><Input value={form.client} onChange={(e) => set("client", e.target.value)} data-testid="project-client" /></div>
          <div><Label>Presupuesto preliminar (€)</Label><Input type="number" value={form.original_budget} onChange={(e) => set("original_budget", e.target.value)} data-testid="project-budget" /></div>
          <div><Label>Inicio previsto</Label><Input type="date" value={form.planned_start} onChange={(e) => set("planned_start", e.target.value)} data-testid="project-start" /></div>
          <div><Label>Fin previsto (baseline)</Label><Input type="date" value={form.baseline_completion} onChange={(e) => set("baseline_completion", e.target.value)} data-testid="project-end" /></div>
          <div className="col-span-2"><Label>Objetivo (charter)</Label><Textarea value={form.objective} onChange={(e) => set("objective", e.target.value)} data-testid="project-objective" /></div>
          <div className="col-span-2"><Label>Alcance inicial (charter)</Label><Textarea value={form.scope} onChange={(e) => set("scope", e.target.value)} data-testid="project-scope" /></div>
        </div>
        <Button className="mt-3 w-full" onClick={submit} disabled={busy || !form.name.trim()} data-testid="project-submit">
          {busy ? "Creando…" : "Crear proyecto"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Proyectos() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = (user?.roles || []).some((r) => r !== "VIEWER");
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "");
  const [phase, setPhase] = useState(params.get("phase") || "");
  const [health, setHealth] = useState(params.get("health") || "");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: items, refetch } = useQuery({
    queryKey: ["proyectos", status, phase, health, q],
    queryFn: async () => (await api.get("/proyectos", { params: { status: status || undefined, phase: phase || undefined, health: health || undefined, q: q || undefined } })).data,
  });

  return (
    <div data-testid="proyectos-page">
      <PageHeader
        title="Proyectos"
        subtitle="De la apertura al cierre: charter, aprobación, fases y puertas de decisión. No hay estados libres."
        actions={canWrite && <Button onClick={() => setOpen(true)} data-testid="new-project-button"><Plus className="mr-1 h-4 w-4" /> Nuevo proyecto</Button>}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por nombre, código, municipio, cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="project-search" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="project-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(PROJECT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={phase || "all"} onValueChange={(v) => setPhase(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="project-phase-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fases</SelectItem>
            {Object.entries(PHASE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={health || "all"} onValueChange={(v) => setHealth(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="project-health-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda salud</SelectItem>
            {Object.entries(PROJECT_HEALTH).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin proyectos" description="Crea el primer proyecto o ajusta los filtros." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proyecto</TableHead><TableHead>Fase</TableHead><TableHead>Estado</TableHead><TableHead>Salud</TableHead>
                <TableHead>Fin baseline</TableHead><TableHead>Fin forecast</TableHead><TableHead className="text-right">Presupuesto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`project-row-${p.id}`} className="cursor-pointer" onClick={() => navigate(`/proyectos/${p.id}`, { state: { crumb: `${p.project_code} — ${p.name}` } })}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="font-mono text-xs text-slate-400">{p.project_code} · {p.municipality || "—"}</p>
                  </TableCell>
                  <TableCell className="text-xs font-medium">{PHASE_LABELS[p.lifecycle_phase]}</TableCell>
                  <TableCell><Chip map={PROJECT_STATUS} value={p.status} testid={`project-status-${p.id}`} /></TableCell>
                  <TableCell><Chip map={PROJECT_HEALTH} value={p.health_status} /></TableCell>
                  <TableCell className="text-xs">{fmtDate(p.baseline_completion)}</TableCell>
                  <TableCell className={`text-xs ${p.current_forecast_completion > p.baseline_completion ? "font-semibold text-red-600" : ""}`}>{fmtDate(p.current_forecast_completion)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{p.current_budget != null ? fmtMoney(p.current_budget) : "restringido"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewProjectDialog open={open} onClose={() => setOpen(false)} onDone={(p) => { setOpen(false); refetch(); qc.invalidateQueries({ queryKey: ["proj-dashboard"] }); navigate(`/proyectos/${p.id}`); }} />
    </div>
  );
}
